/**
 * ConfigAuthPage
 *
 * Admin page to configure the authentication service for this org.
 * Manages the plain fields (adapter_type, tenant_id, IDP project details) and
 * the two GCCS credential atoms (IDP API key, JWT signing key) stored in
 * organisation/{org_id}/config/auth in Firestore.
 *
 * Route: /settings/config/auth  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, CompactButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import {
  getServiceConfig,
  updateServiceConfig,
  getAuthCredential,
  updateAuthCredential,
  resetAuthCredential,
  sealAuthCredential,
  activateJwtRotation,
  completeJwtRotation,
  completeCancelledJwtRotation,
} from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";

const ADAPTER_OPTIONS = [
  { value: "idp",      label: "Identity Platform (GCP)" },
  { value: "firebase", label: "Firebase Auth"            },
  { value: "local",    label: "Local (no external IDP)"  },
];

const EMPTY_FORM = {
  adapter_type:    "idp",
  tenant_id:       "",
  idp_project_id:  "",
  idp_auth_domain: "",
  idp_message_id:  "",
  idp_app_id:      "",
};

const ConfigAuthPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);
  const [idpStatus, setIdpStatus] = useState({ configured: false, last4: "" });
  const [idpHasPlaintext, setIdpHasPlaintext] = useState(false);
  const [idpApiKey, setIdpApiKey] = useState("");
  const [editingIdp, setEditingIdp] = useState(false);
  const [idpCredentialAction, setIdpCredentialAction] = useState("");
  const [jwtHasPlaintext, setJwtHasPlaintext] = useState(false);
  const [jwtStatus, setJwtStatus] = useState({ configured: false, rotation_state: "idle" });
  const [jwtSigningKey, setJwtSigningKey] = useState("");
  const [editingJwt, setEditingJwt] = useState(false);
  const [jwtCredentialAction, setJwtCredentialAction] = useState("");
  const [jwtAction, setJwtAction] = useState("");
  const [confirmingSave, setConfirmingSave] = useState(false);
  const [sealingIdp, setSealingIdp]           = useState(false);
  const [sealingJwt, setSealingJwt]           = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("auth");
      if (data.configured) {
        const loaded = {
          adapter_type:    data.adapter_type    ?? "idp",
          tenant_id:       data.tenant_id       ?? "",
          idp_project_id:  data.idp_project_id  ?? "",
          idp_auth_domain: data.idp_auth_domain ?? "",
          idp_message_id:  data.idp_message_id  ?? "",
          idp_app_id:      data.idp_app_id      ?? "",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load auth config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target ? e.target.value : e }));

  const fetchIdpStatus = useCallback(async () => {
    const data = await getAuthCredential("idp");
    setIdpHasPlaintext(!!data.has_plaintext);
    setIdpStatus(data);
    return data;
  }, []);

  const fetchJwtStatus = useCallback(async () => {
    const data = await getAuthCredential("jwt");
    setJwtHasPlaintext(!!data.has_plaintext);
    setJwtStatus(data);
    return data;
  }, []);

  useEffect(() => {
    if (orgAvailable) {
      fetchIdpStatus();
      fetchJwtStatus();
    }
  }, [orgAvailable, fetchIdpStatus, fetchJwtStatus]);

  const handleSealIdp = async () => {
    setSealingIdp(true);
    try {
      await sealAuthCredential("idp");
      setIdpHasPlaintext(false);
      showFeedback("IDP API key sealed successfully.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to seal IDP API key.", "error");
    } finally {
      setSealingIdp(false);
    }
  };

  const handleSealJwt = async () => {
    setSealingJwt(true);
    try {
      await sealAuthCredential("jwt");
      await fetchJwtStatus();
      showFeedback("JWT signing key sealed successfully.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to seal JWT signing key.", "error");
    } finally {
      setSealingJwt(false);
    }
  };

  const handlePrepareJwtRotation = () => {
    setJwtCredentialAction("rotate");
    setEditingJwt(false);
  };

  const handleActivateJwtRotation = async () => {
    setJwtAction("activate");
    try {
      await activateJwtRotation();
      await fetchJwtStatus();
      showFeedback("JWT key activated. Deploy photon-api to begin signing with it.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to activate JWT rotation.", "error");
    } finally {
      setJwtAction("");
    }
  };

  const handleDeleteJwtRotationKey = async () => {
    setJwtAction("delete");
    try {
      await resetAuthCredential("jwt");
      await fetchJwtStatus();
      showFeedback(
        jwtStatus.rotation_state === "prepared"
          ? "Prepared JWT key cancelled. Deploy photon-api and photon-ws to remove it from trusted key rings."
          : "Previous JWT key retired. Deploy photon-api and photon-ws.",
        "success"
      );
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to update JWT rotation.", "error");
    } finally {
      setJwtAction("");
    }
  };

  const handleCompleteJwtCancellation = async () => {
    setJwtAction("cancel-complete");
    try {
      await completeCancelledJwtRotation();
      await fetchJwtStatus();
      showFeedback("Cancelled JWT key rollout completed.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to complete JWT cancellation.", "error");
    } finally {
      setJwtAction("");
    }
  };

  const handleCompleteJwtRollout = async () => {
    setJwtAction("complete");
    try {
      await completeJwtRotation();
      await fetchJwtStatus();
      showFeedback("API rollout confirmed. The seven-day key overlap has started.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to confirm JWT rollout.", "error");
    } finally {
      setJwtAction("");
    }
  };

  const discardDrafts = () => {
    setForm(originalForm);
    setIdpApiKey("");
    setEditingIdp(false);
    setIdpCredentialAction("");
    setJwtSigningKey("");
    setEditingJwt(false);
    setJwtCredentialAction("");
  };

  const persistChanges = async () => {
    setSaving(true);
    try {
      if (isDirty(form, originalForm)) {
        await updateServiceConfig("auth", form);
      }
      if (idpCredentialAction === "rotate") {
        await updateAuthCredential("idp", { api_key: idpApiKey });
      } else if (idpCredentialAction === "revoke") {
        await resetAuthCredential("idp");
      }
      if (jwtCredentialAction === "rotate") {
        await updateAuthCredential("jwt", { signing_key: jwtSigningKey });
      } else if (jwtCredentialAction === "revoke") {
        await resetAuthCredential("jwt");
      }
      setOriginalForm(form);
      setIdpApiKey("");
      setEditingIdp(false);
      setIdpCredentialAction("");
      setJwtSigningKey("");
      setEditingJwt(false);
      setJwtCredentialAction("");
      await Promise.all([fetchIdpStatus(), fetchJwtStatus()]);
      showFeedback(
        "Auth configuration saved. Deploy the affected services to apply credential changes.",
        "success"
      );
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save auth config.", "error");
    } finally {
      setSaving(false);
      setConfirmingSave(false);
    }
  };

  const credentialsDirty = !!idpCredentialAction || !!jwtCredentialAction;
  const pageDirty = isDirty(form, originalForm) || credentialsDirty;
  const handleSave = () => {
    if (credentialsDirty) {
      setConfirmingSave(true);
      return;
    }
    persistChanges();
  };

  return (
    <SettingsPageShell
      title="Authentication Configuration"
      description="Configure the identity provider adapter, tenant, and IDP credentials for this organisation."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">

          {/* Impact warning */}
          <div className="flex items-start gap-2 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 border border-edge-amber-200 dark:border-edge-amber-700 rounded-lg px-3 py-2.5">
            <AlertTriangle className="w-4 h-4 text-ink-hex-d97706 dark:text-ink-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-ink-amber-800 dark:text-ink-amber-300 leading-relaxed">
              Configuration and IDP API key changes require an API deployment. JWT rotation uses
              a staged key overlap across photon-api and photon-ws revisions.
            </p>
          </div>

          {/* Adapter type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Auth Adapter
            </label>
            <Select
              value={form.adapter_type}
              onChange={(v) => setForm((prev) => ({ ...prev, adapter_type: v }))}
              options={ADAPTER_OPTIONS}
            />
          </div>

          {/* IDP-specific fields — hidden for local adapter */}
          {form.adapter_type !== "local" && (
            <>
              {/* Tenant ID */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Tenant ID
                </label>
                <Input
                  type="text"
                  placeholder="tenant-xxxx"
                  value={form.tenant_id}
                  onChange={set("tenant_id")}
                />
                <p className="text-[11px] text-ink-gray-400">
                  Required for Identity Platform. Optional for Firebase project-level authentication.
                </p>
              </div>

              {/* IDP Project ID */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  IDP Project ID
                </label>
                <Input
                  type="text"
                  placeholder="my-gcp-project"
                  value={form.idp_project_id}
                  onChange={set("idp_project_id")}
                />
                <p className="text-[11px] text-ink-gray-400">
                  Firebase / Identity Platform GCP project ID. Usually the same as your main GCP project.
                </p>
              </div>

              {/* IDP Auth Domain */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Auth Domain
                </label>
                <Input
                  type="text"
                  placeholder="my-gcp-project.firebaseapp.com"
                  value={form.idp_auth_domain}
                  onChange={set("idp_auth_domain")}
                />
                <p className="text-[11px] text-ink-gray-400">
                  Firebase Auth domain. Format: <code className="font-mono">project-id.firebaseapp.com</code>
                </p>
              </div>

              {/* Messaging Sender ID */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Messaging Sender ID
                </label>
                <Input
                  type="text"
                  placeholder="123456789012"
                  value={form.idp_message_id}
                  onChange={set("idp_message_id")}
                />
                <p className="text-[11px] text-ink-gray-400">
                  Firebase Cloud Messaging sender ID. Found in Firebase Console → Project Settings → General → Cloud Messaging.
                </p>
              </div>

              {/* App ID */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  App ID
                </label>
                <Input
                  type="text"
                  placeholder="1:123456789012:web:abc123"
                  value={form.idp_app_id}
                  onChange={set("idp_app_id")}
                />
                <p className="text-[11px] text-ink-gray-400">
                  Firebase app registration ID. Found in Firebase Console → Project Settings → Your apps.
                </p>
              </div>
            </>
          )}

          {/* IDP API Key credential — only for external adapters */}
          {form.adapter_type !== "local" && (
            idpHasPlaintext ? (
              <div className="flex flex-col gap-2 rounded-[10px] border border-edge-amber-400/60 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 p-4">
                <p className="text-xs font-semibold text-ink-amber-700 dark:text-ink-amber-400">IDP API Key — plaintext detected</p>
                <p className="text-[11px] text-ink-amber-700/80 dark:text-ink-amber-400/70">
                  This key was written as plaintext by the setup script. Seal it into secure storage.
                </p>
                <div>
                  <CompactButton onClick={handleSealIdp} disabled={sealingIdp}>
                    {sealingIdp ? "Sealing…" : "Seal key"}
                  </CompactButton>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 p-4">
                <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">IDP API Key</p>
                <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                  Firebase / Identity Platform Web API key from the Identity Platform host project.
                </p>
                {idpCredentialAction === "revoke" ? (
                  <>
                    <p className="text-xs text-ink-red-600 dark:text-ink-red-400">Pending deletion. Save to revoke this key.</p>
                    <div><CompactButton onClick={() => setIdpCredentialAction("")}>Undo</CompactButton></div>
                  </>
                ) : idpCredentialAction === "rotate" ? (
                  <>
                    <p className="text-xs text-ink-amber-700 dark:text-ink-amber-400">Replacement staged. Save to persist it.</p>
                    <div><CompactButton onClick={() => { setIdpCredentialAction(""); setIdpApiKey(""); }}>Undo</CompactButton></div>
                  </>
                ) : editingIdp ? (
                  <>
                    <Input type="password" placeholder="Enter IDP API key" value={idpApiKey} onChange={(event) => setIdpApiKey(event.target.value)} />
                    <div className="flex gap-2">
                      <PrimaryButton onClick={() => { setIdpCredentialAction("rotate"); setEditingIdp(false); }} disabled={!idpApiKey.trim()}>
                        Stage key
                      </PrimaryButton>
                      <CompactButton onClick={() => { setEditingIdp(false); setIdpApiKey(""); }}>Cancel</CompactButton>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                      {idpStatus.configured ? `Configured${idpStatus.last4 ? ` (••••${idpStatus.last4})` : ""}.` : "Not configured."}
                    </p>
                    <div className="flex gap-2">
                      <CompactButton onClick={() => setEditingIdp(true)}>{idpStatus.configured ? "Rotate" : "Add key"}</CompactButton>
                      {idpStatus.configured && <CompactButton onClick={() => setIdpCredentialAction("revoke")}>Revoke</CompactButton>}
                    </div>
                  </>
                )}
              </div>
            )
          )}

          {/* JWT Signing Key credential — always required */}
          {jwtHasPlaintext ? (
            <div className="flex flex-col gap-2 rounded-[10px] border border-edge-amber-400/60 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 p-4">
              <p className="text-xs font-semibold text-ink-amber-700 dark:text-ink-amber-400">JWT Signing Key — plaintext detected</p>
              <p className="text-[11px] text-ink-amber-700/80 dark:text-ink-amber-400/70">
                This key was written as plaintext by the setup script. Seal it into secure storage.
              </p>
              <div>
                <CompactButton onClick={handleSealJwt} disabled={sealingJwt}>
                  {sealingJwt ? "Sealing…" : "Seal key"}
                </CompactButton>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 p-4">
              <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                JWT Signing Key
              </p>
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                {jwtStatus.configured ? "Configured." : "Not configured."}
              </p>
              {jwtStatus.rotation_state === "prepared" ? (
                <>
                  <p className="text-[11px] text-ink-amber-700 dark:text-ink-amber-400">
                    Pending key prepared. Deploy photon-api and photon-ws, then activate it.
                  </p>
                  <div className="flex gap-2">
                    <CompactButton onClick={handleActivateJwtRotation} disabled={!!jwtAction}>
                      {jwtAction === "activate" ? "Activating…" : "Activate after deployment"}
                    </CompactButton>
                    <CompactButton onClick={handleDeleteJwtRotationKey} disabled={!!jwtAction}>
                      {jwtAction === "delete" ? "Cancelling…" : "Cancel"}
                    </CompactButton>
                  </div>
                </>
              ) : jwtStatus.rotation_state === "activating" ? (
                <>
                  <p className="text-[11px] text-ink-amber-700 dark:text-ink-amber-400">
                    New key selected. Deploy photon-api and wait for the rollout to finish before confirming.
                  </p>
                  <div>
                    <CompactButton onClick={handleCompleteJwtRollout} disabled={!!jwtAction}>
                      {jwtAction === "complete" ? "Confirming…" : "I confirm API rollout is complete"}
                    </CompactButton>
                  </div>
                </>
              ) : jwtStatus.rotation_state === "cancellation_pending" ? (
                <>
                  <p className="text-[11px] text-ink-amber-700 dark:text-ink-amber-400">
                    Pending key cancelled. Deploy photon-api and photon-ws, then confirm cleanup.
                  </p>
                  <div>
                    <CompactButton onClick={handleCompleteJwtCancellation} disabled={!!jwtAction}>
                      {jwtAction === "cancel-complete" ? "Confirming…" : "I confirm cleanup rollout is complete"}
                    </CompactButton>
                  </div>
                </>
              ) : jwtStatus.rotation_state === "overlap" ? (
                <>
                  <p className="text-[11px] text-ink-amber-700 dark:text-ink-amber-400">
                    New key active. Deploy photon-api now. The old key remains valid until {jwtStatus.retire_after || "the overlap period ends"}.
                  </p>
                  <div>
                    <CompactButton onClick={handleDeleteJwtRotationKey} disabled={!!jwtAction}>
                      {jwtAction === "delete" ? "Retiring…" : "Retire old key"}
                    </CompactButton>
                  </div>
                </>
              ) : jwtCredentialAction === "revoke" ? (
                <>
                  <p className="text-xs text-ink-red-600 dark:text-ink-red-400">Pending deletion. Save to revoke this key.</p>
                  <div><CompactButton onClick={() => setJwtCredentialAction("")}>Undo</CompactButton></div>
                </>
              ) : jwtCredentialAction === "rotate" ? (
                <>
                  <p className="text-xs text-ink-amber-700 dark:text-ink-amber-400">
                    {jwtStatus.configured ? "Rotation staged. Save to prepare the new key." : "Signing key staged. Save to configure it."}
                  </p>
                  <div><CompactButton onClick={() => { setJwtCredentialAction(""); setJwtSigningKey(""); }}>Undo</CompactButton></div>
                </>
              ) : editingJwt ? (
                <>
                  <Input
                    type="password"
                    placeholder="Enter new signing key"
                    value={jwtSigningKey}
                    onChange={(event) => setJwtSigningKey(event.target.value)}
                  />
                  <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                    Use at least 32 bytes of randomly generated secret material.
                  </p>
                  <div className="flex gap-2">
                    <PrimaryButton
                      onClick={handlePrepareJwtRotation}
                      disabled={jwtSigningKey.trim().length < 32}
                    >
                      Stage key
                    </PrimaryButton>
                    <CompactButton onClick={() => { setEditingJwt(false); setJwtSigningKey(""); }}>Cancel</CompactButton>
                  </div>
                </>
              ) : (
                <div className="flex gap-2">
                  <CompactButton onClick={() => setEditingJwt(true)}>
                    {jwtStatus.configured ? "Rotate" : "Add key"}
                  </CompactButton>
                  {jwtStatus.configured && <CompactButton onClick={() => setJwtCredentialAction("revoke")}>Revoke</CompactButton>}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !pageDirty}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
            <CompactButton onClick={discardDrafts} disabled={saving || !pageDirty}>Cancel</CompactButton>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmingSave}
        title="Save authentication changes?"
        confirmLabel="Save changes"
        loadingLabel="Saving…"
        confirmVariant={idpCredentialAction === "revoke" || jwtCredentialAction === "revoke" ? "danger" : "primary"}
        isLoading={saving}
        onCancel={() => setConfirmingSave(false)}
        onConfirm={persistChanges}
      >
        <div className="space-y-2 text-left">
          {idpCredentialAction === "rotate" && <p>The IDP API key will be replaced. Deploy photon-api to use it.</p>}
          {idpCredentialAction === "revoke" && <p>The IDP API key will be deleted. Login and password reset will stop after photon-api restarts.</p>}
          {jwtCredentialAction === "rotate" && <p>{jwtStatus.configured ? "A JWT rotation will be prepared." : "The JWT signing key will be configured."} Deploy photon-api and photon-ws afterward.</p>}
          {jwtCredentialAction === "revoke" && <p>All JWT signing key material will be deleted. JWT issuance and validation will stop after photon-api and photon-ws restart.</p>}
        </div>
      </ConfirmDialog>
    </SettingsPageShell>
  );
};

export default ConfigAuthPage;
